// Atlas buildings (ATL-5). PURE (ENG-1).
//
// A footprint polygon → a hollow multi-storey building: brick perimeter walls
// (stone brick when levels ≥ 4) to levels×4 high, air interior, a door facing the
// nearest road, glass windows on every 3rd perimeter block (upper 2 rows), an
// oak-slab floor every 4 blocks with a 2×2 stair opening + ladder column at the
// NW-most interior corner, a glowstone light per floor, and a flat slab-lipped
// roof. Precompute the plan once (per generator), then stamp only the chunk cells.
import { WORLD } from '../config';
import { pointInPolygon, boundsOf, type Building, type Pt, type BBox } from './intermediate';

/** Block-state ids the building stamper writes. */
export interface BuildingIds {
  air: number;
  brick: number;
  stoneBrick: number;
  glass: number;
  slab: number;    // oak_slab (floors + roof lip)
  ladder: number;
  glowstone: number;
}

export interface BuildingPlan {
  bbox: BBox;
  levels: number;
  height: number;
  wall: number;
  /** "x,z" footprint cells. */
  cells: Set<string>;
  perimeter: Set<string>;
  interior: Set<string>;
  door: Pt;
  nw: Pt;      // NW-most interior cell (ladder shaft origin)
  center: Pt;  // interior centre (glowstone column)
  /** Foundation Y = min ground over the footprint (ELV-4); set by the rasterizer
   *  once elevation is known, default `NaN` (the caller passes an explicit floorY). */
  floorY: number;
}

const key = (x: number, z: number): string => `${x},${z}`;

/** Precompute a building's cell sets + door / ladder / centre positions (ATL-5). */
export function buildBuildingPlan(b: Building, ids: BuildingIds, nearestRoad: Pt | null): BuildingPlan | null {
  const bb = boundsOf(b.footprint);
  const cells = new Set<string>();
  for (let z = Math.floor(bb.minZ); z <= Math.ceil(bb.maxZ); z++) {
    for (let x = Math.floor(bb.minX); x <= Math.ceil(bb.maxX); x++) {
      if (!pointInPolygon(x + 0.5, z + 0.5, b.footprint)) continue;
      if (b.holes?.some((h) => pointInPolygon(x + 0.5, z + 0.5, h))) continue; // courtyard (E-20)
      cells.add(key(x, z));
    }
  }
  if (cells.size < 9) return null; // too small to be a building

  const perimeter = new Set<string>();
  const interior = new Set<string>();
  for (const c of cells) {
    const [x, z] = c.split(',').map(Number) as [number, number];
    const edge = !cells.has(key(x + 1, z)) || !cells.has(key(x - 1, z)) || !cells.has(key(x, z + 1)) || !cells.has(key(x, z - 1));
    if (edge) perimeter.add(c); else interior.add(c);
  }
  if (interior.size === 0) return null;

  // NW-most interior cell (north = −z, west = −x): min z, then min x.
  let nw: Pt = [Infinity, Infinity];
  let center: Pt = [0, 0];
  let sx = 0, sz = 0;
  for (const c of interior) {
    const [x, z] = c.split(',').map(Number) as [number, number];
    if (z < nw[1] || (z === nw[1] && x < nw[0])) nw = [x, z];
    sx += x; sz += z;
  }
  // Interior cell nearest the centroid = the glowstone column.
  const gcx = sx / interior.size, gcz = sz / interior.size;
  let bestD = Infinity;
  for (const c of interior) {
    const [x, z] = c.split(',').map(Number) as [number, number];
    const d = (x - gcx) ** 2 + (z - gcz) ** 2;
    if (d < bestD) { bestD = d; center = [x, z]; }
  }

  // Door: perimeter cell nearest the road (fallback: nearest the centroid).
  const target: Pt = nearestRoad ?? [gcx, gcz];
  let door: Pt = center;
  bestD = Infinity;
  for (const c of perimeter) {
    const [x, z] = c.split(',').map(Number) as [number, number];
    const d = (x - target[0]) ** 2 + (z - target[1]) ** 2;
    if (d < bestD) { bestD = d; door = [x, z]; }
  }

  const levels = b.levels;
  const wall = levels >= 4 ? ids.stoneBrick : ids.brick;
  return { bbox: bb, levels, height: levels * 4, wall, cells, perimeter, interior, door, nw, center, floorY: NaN };
}

/** Stamp the parts of `plan` that fall inside chunk (cx,cz). The building floor sits
 *  at `floorY` (min ground over the footprint, ELV-4); on relief, `groundAt` gives a
 *  cell's own ground so perimeter walls extend downward to fill any gap — no floating
 *  or half-buried perimeters (E-27). `groundAt` defaults to a flat `floorY` (parity). */
export function stampBuilding(
  plan: BuildingPlan,
  cx: number,
  cz: number,
  floorY: number,
  ids: BuildingIds,
  set: (lx: number, y: number, lz: number, state: number) => void,
  groundAt: (x: number, z: number) => number = () => floorY,
): void {
  const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
  const inChunk = (x: number, z: number): boolean => x >= bx && x < bx + WORLD.SECTION && z >= bz && z < bz + WORLD.SECTION;
  const put = (x: number, y: number, z: number, state: number): void => {
    if (y < WORLD.MIN_Y || y > WORLD.MAX_Y || !inChunk(x, z)) return;
    set(x - bx, y, z - bz, state);
  };

  const top = floorY + plan.height;
  const doorK = key(plan.door[0], plan.door[1]);
  const nwSet = new Set([key(plan.nw[0], plan.nw[1]), key(plan.nw[0] + 1, plan.nw[1]), key(plan.nw[0], plan.nw[1] + 1), key(plan.nw[0] + 1, plan.nw[1] + 1)]);

  // Iterate only the footprint cells that fall inside this chunk (membership test)
  // rather than the whole (possibly large) footprint — keeps chunk-pull cheap.
  const x0 = Math.max(bx, Math.floor(plan.bbox.minX)), x1 = Math.min(bx + WORLD.SECTION - 1, Math.ceil(plan.bbox.maxX));
  const z0 = Math.max(bz, Math.floor(plan.bbox.minZ)), z1 = Math.min(bz + WORLD.SECTION - 1, Math.ceil(plan.bbox.maxZ));
  for (let z = z0; z <= z1; z++) {
    for (let x = x0; x <= x1; x++) {
      const c = key(x, z);
      if (!plan.cells.has(c)) continue;
      const isPerim = plan.perimeter.has(c);
      const isDoor = c === doorK;
      const isNW = nwSet.has(c);

      // Clear the column (interior air) then lay walls / floors / roof. The carve
      // starts at the cell's own ground so terrain that pokes above the floor on
      // higher parts of a sloped footprint is removed (no dirt inside the room).
      const cellGround = Math.min(groundAt(x, z), top);
      for (let y = floorY + 1; y <= Math.max(top, cellGround); y++) put(x, y, z, ids.air);

      if (isPerim) {
        // Foundation skirt: wall material from the floor down to the cell's ground,
        // filling any gap so no perimeter block floats over lower terrain (E-27).
        for (let y = floorY; y > cellGround; y--) put(x, y, z, plan.wall);
        for (let y = floorY + 1; y <= top; y++) {
          const rel = y - (floorY + 1);
          const floor = Math.floor(rel / 4);
          const rowInFloor = rel - floor * 4;
          // Door: a 2-tall gap above the ground-floor slab at the door cell.
          if (isDoor && (rel === 1 || rel === 2)) continue;
          // Windows: every 3rd perimeter block, upper 2 rows of each storey → glass.
          const window = ((x + z) % 3 === 0) && rowInFloor >= 2 && floor < plan.levels;
          put(x, y, z, window ? ids.glass : plan.wall);
        }
      }

      // Floor slabs every 4 blocks, with a 2×2 opening at the NW shaft (floors ≥ 1).
      for (let f = 0; f < plan.levels; f++) {
        if (f >= 1 && isNW) continue; // stair opening
        put(x, floorY + 1 + f * 4, z, ids.slab);
      }

      // Flat roof (solid) + a slab lip one block above the perimeter.
      put(x, top, z, plan.wall);
      if (isPerim) put(x, top + 1, z, ids.slab);
    }
  }

  // Ladder shaft (NW-most interior corner) + glowstone light per floor (single
  // columns; put() clips to this chunk, so a shaft in a neighbour chunk is skipped).
  for (let y = floorY + 1; y < top; y++) put(plan.nw[0], y, plan.nw[1], ids.ladder);
  for (let f = 0; f < plan.levels; f++) put(plan.center[0], floorY + 4 * (f + 1), plan.center[1], ids.glowstone);
}
